import Papa from 'papaparse';
import { supabase } from '../../../lib/supabase';
import { keyOf } from './conversions';
import { unwrap } from './transferOrders';

/*
  BACK INTO CLAIMSOFT. Ordering stage 6, 12 October 2026, on his word: "then
  start stage 6".

  **Two files per location, and the order is a constraint**, his walkthrough
  of 4 October (docs/orders/DOOR-4-THE-CLAIMSOFT-RUN.md, step 5):

      1. the PO file    every line bought from a vendor
      2. the TO file    every line moved from Austin

  **The PO file must go first**: ClaimSoft matches on the original transfer
  order number, and the TO file replaces it with the Account Edge customer sale
  number.

  **Each file is the original transfer order, carried through whole**, his
  correction of 4 October: *"all the data that was on the file at the begining
  would still need to be on the sheet with the exception of deletion or qty
  changes made during the process."* So every column of every line is written
  back as ClaimSoft sent it (kept on the line as `source_row`), except:

      a skipped line            deleted
      a changed item or qty     ItemNumber and QuantityOrdered changed
      bought from a vendor      PONumber = the Account Edge PO number
                                ConvertToPO = Yes (his answer, 12 October)
                                POQuantity = the quantity
      moved from Austin         NewTONumber = the Austin sale's number

  **TONumber is never touched**: ClaimSoft matches the import on it. The
  columns are in his transfer order's own order; the file is plain, as
  ClaimSoft wrote it (no byte-order mark, one line ending per row).
*/

export const TO_COLUMNS = ['TONumber', 'NewTONumber', 'PONumber', 'Status', 'SourceWarehouse', 'DestinationWarehouse', 'Notes', 'CreatedBy', 'CreatedDate', 'TOModifiedBy', 'TOModifiedDate', 'DateReleased', 'DateSent', 'DateClosed', 'ItemNumber', 'SerialNumber', 'Location', 'Bin', 'Description', 'Vendor', 'QuantityOrdered', 'QuantityReceived', 'ModifiedBy', 'ModifiedDate', 'ReceivedBy', 'DateReceived', 'ConvertToPO', 'POQuantity'];

/*
  **Every number he still has to type**, from the customer sales the saved run
  makes and the numbers already kept. Pure.
*/
export function missingNumbers(sales, kept) {
  const out = [];
  for (const s of sales) {
    const k = kept.get(s.sale_key) || {};
    if (s.austin && !String(k.sale_number || '').trim()) out.push('the customer sale number for the sale from Austin');
    if (!s.austin && !String(k.po_number || '').trim()) out.push('the ' + s.name + ' PO number');
  }
  return out;
}

/*
  **The two files**, pure. `lines` are the job's saved lines, `sales` from
  `planSales`, `kept` the typed numbers by sale. A file with no lines is null.
*/
export function claimSoftFiles({ lines, sales, kept }) {
  const saleOfLine = new Map();
  for (const s of sales) for (const x of s.lines) saleOfLine.set(x.id, s);
  const extra = [];
  for (const l of lines) for (const k of Object.keys(l.source_row || {})) if (!TO_COLUMNS.includes(k) && !extra.includes(k)) extra.push(k);
  const columns = [...TO_COLUMNS, ...extra];
  const po = [], to = [];
  for (const l of [...lines].sort((a, b) => a.line_no - b.line_no)) {
    const s = saleOfLine.get(l.id);
    if (!s) continue;                                   // skipped, or nothing to send: deleted
    const k = kept.get(s.sale_key) || {};
    const row = { ...(l.source_row || {}) };
    const qty = Number(l.final_quantity);
    if (keyOf(l.final_item_number) !== keyOf(unwrap(row.ItemNumber))) row.ItemNumber = l.final_item_number;
    row.QuantityOrdered = String(qty);
    if (s.austin) {
      row.NewTONumber = String(k.sale_number || '').trim();
      to.push(row);
    } else {
      row.PONumber = String(k.po_number || '').trim();
      row.ConvertToPO = 'Yes';
      row.POQuantity = String(qty);
      po.push(row);
    }
  }
  const csv = rows => (rows.length ? Papa.unparse({ fields: columns, data: rows.map(r => columns.map(c => (r[c] == null ? '' : r[c]))) }, { newline: '\n' }) : null);
  return { po: csv(po), to: csv(to), poLines: po.length, toLines: to.length };
}

/* The numbers kept for a job, by sale. */
export async function loadKept(jobId) {
  const { data, error } = await supabase.from('order_job_sales').select('id, sale_key, vendor_id, is_austin, sale_number, po_number, entered_at').eq('job_id', jobId);
  return { kept: new Map((data || []).map(r => [r.sale_key, r])), error };
}

/* **One number, saved as he types it**, made or updated by sale. Proved by the
   row coming back. */
export async function keepNumber({ jobId, sale, field, value, userId }) {
  const row = {
    job_id: jobId, sale_key: sale.sale_key, vendor_id: sale.vendor_id || null, is_austin: !!sale.austin,
    [field]: String(value || '').trim() || null, entered_by: userId || null, entered_at: new Date().toISOString(), updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase.from('order_job_sales').upsert(row, { onConflict: 'job_id,sale_key' }).select('id, sale_key, sale_number, po_number').maybeSingle();
  if (error) return { ok: false, error };
  if (!data) return { ok: false, error: { message: 'That number did not save.' } };
  return { ok: true, row: data };
}

/* A download, named so the two sort in the order they must be imported. */
export function downloadText(text, name) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
}

export function fileNames(job) {
  const to = String(job.source_reference || job.reference).replace(/^TO\s*/i, 'TO ');
  const office = (job.destination && (job.destination.onhand_name || job.destination.name)) || '';
  return {
    po: '1 ClaimSoft PO file ' + to + ' ' + office + '.csv',
    to: '2 ClaimSoft TO file ' + to + ' ' + office + '.csv',
  };
}
